/**
 * Liga o outbox à interface.
 *
 * Três gatilhos de drenagem, todos aprovados — e todos explícitos, porque o
 * iOS não tem Background Sync:
 *   1. abrir ou retomar o app;
 *   2. a rede voltar com o app ativo;
 *   3. o botão "Sincronizar agora".
 */

import type { Mutation } from '@nv/offline';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { onUnauthorized, useAuthStore } from '../auth/store';
import { localDb } from '../features/roteiro/persistence';
import { aplicarAceiteDaBagagem } from '../features/bagagem/store';
import { aplicarAceite } from '../features/roteiro/store';
import { lerViagemAtiva } from '../trip/viagemAtiva';
import { createSender } from './client';
import { useSyncStore } from './store';

/**
 * A fila drena para a viagem ATIVA.
 *
 * Ela é lida a cada drenagem, e não uma vez na montagem: trocar de viagem em
 * "Minhas viagens" precisa mudar para onde as mutações vão, e uma configuração
 * capturada no primeiro render mandaria a despesa nova para a viagem anterior.
 */
function configuracaoAtual(userId: string) {
  return {
    // Mesma origem: o proxy do Vite em desenvolvimento reproduz o proxy do
    // host (Nginx) que serve staging e produção.
    baseUrl: '',
    // Por conta: a fila de uma conta nunca drena para a viagem de outra.
    tripId: lerViagemAtiva(userId)?.id ?? '',
    // Sessão expirada não descarta o outbox: marca a sessão como encerrada e a
    // fila espera o próximo login.
    onUnauthorized,
    // Versão aceita volta para o estado local — ver `aplicarAceite`.
    onAccepted: async (mutation: Mutation, body: unknown) => {
      await aplicarAceite(mutation, body);
      await aplicarAceiteDaBagagem(mutation, body);
    },
  };
}

export function useSync() {
  const syncNow = useSyncStore((s) => s.syncNow);
  const refreshCounts = useSyncStore((s) => s.refreshCounts);
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const cliente = useQueryClient();

  const sincronizar = useCallback(async () => {
    const config = configuracaoAtual(userId);
    // Sem viagem escolhida não há para onde sincronizar — e a fila espera, em
    // vez de tentar contra uma URL sem id.
    if (!config.tripId) return;
    const db = await localDb();
    await syncNow(db.outbox, createSender(config));
    // O servidor mudou: o que foi lido dele antes está velho. Invalidar tudo é
    // mais barato do que saber qual tela mostra o quê — e a Home é a que mais
    // sofre: sem isto, a atividade recém-criada só aparecia depois de recarregar.
    await cliente.invalidateQueries();
  }, [syncNow, userId, cliente]);

  const recontar = useCallback(async () => {
    const db = await localDb();
    await refreshCounts(db.outbox);
  }, [refreshCounts]);

  useEffect(() => {
    void recontar();
    // Ao abrir o app já tenta drenar: no iOS, nada acontece em background.
    void sincronizar();

    const aoVoltarRede = () => void sincronizar();
    const aoRetomar = () => {
      if (document.visibilityState === 'visible') void sincronizar();
    };
    // Enfileirou com rede: envia já. Sem isto, uma atividade criada online
    // ficava "pendente" até alguém tocar em "Sincronizar agora" ou trocar de
    // app — burocracia sem motivo. Sem rede, a fila espera o `online`.
    const aoMudarOutbox = () => {
      void recontar();
      if (navigator.onLine) void sincronizar();
    };

    window.addEventListener('online', aoVoltarRede);
    document.addEventListener('visibilitychange', aoRetomar);
    window.addEventListener('nv:outbox-changed', aoMudarOutbox);

    return () => {
      window.removeEventListener('online', aoVoltarRede);
      document.removeEventListener('visibilitychange', aoRetomar);
      window.removeEventListener('nv:outbox-changed', aoMudarOutbox);
    };
  }, [sincronizar, recontar]);

  return { sincronizar, recontar };
}
